import { createReducer } from '@reduxjs/toolkit'
import { LastBar } from 'store/market/types'
import { SymbolName } from 'types'
import { formatOrderbookMessage } from '../../utils/orderbook/order'
import {
  clearOrderbookAction,
  getOrderbookAction,
  getRecentHistoryAction,
  incrementalOrderbookAction,
  unshiftTickAction,
  updateBinanceLastBar,
  updateBinanceTimeConnect,
  updateCurrentChartTab,
  updateHideOtherPairs,
  updateOrderbook,
  updateOrderbookPrecisionAction,
  updateResolution,
  updateShowListType,
  updateChartType,
  updateFavoriteResloution,
} from './actions'
import { OrderbookData, ShowListType, Tick } from './types'
import { insertAndMaintainOrder } from 'utils/orderbook/insert'
import { SelectIntervalsValue } from 'constants/chart'

export enum TradingViewKLintType {
  ZKEX = 'ZKEX',
  BINANCE = 'BINANCE',
  INFO = 'INFO',
}
export interface OrderBookState {
  currentChartTab: TradingViewKLintType
  hideOtherPairs: boolean
  binanceTimeConnect: number
  resolution: string
  orderbook: OrderbookData
  precision: string
  showListType: ShowListType
  binanceLastBar: LastBar
  tickList: Record<SymbolName, Tick[]>
  chartType: number
  favoriteResolution: Array<string>
}
export const initialState: OrderBookState = {
  currentChartTab: TradingViewKLintType.BINANCE,
  hideOtherPairs: false,
  binanceTimeConnect: 0,
  resolution: '15',
  orderbook: {} as OrderbookData,
  precision: '0.01',
  showListType: ShowListType.all,
  binanceLastBar: {
    symbol: '',
    time: 0,
    open: 0,
    close: 0,
    low: 0,
    high: 0,
    volume: 0,
    isBarClosed: false,
  },
  tickList: {},
  chartType: 1,
  favoriteResolution: SelectIntervalsValue,
}
export default createReducer<OrderBookState>(initialState, (builder) => {
  builder
    .addCase(updateCurrentChartTab, (state, { payload }) => {
      state.currentChartTab = payload
    })
    .addCase(updateBinanceLastBar, (state, { payload }) => {
      if (payload?.t) {
        state.binanceLastBar = {
          symbol: payload?.s,
          time: payload?.t,
          low: payload?.l,
          high: payload?.h,
          open: payload?.o,
          close: payload?.c,
          volume: payload?.v,
          isBarClosed: false,
        }
      } else {
        state.binanceLastBar = payload
      }
    })
    .addCase(updateBinanceTimeConnect, (state, { payload }) => {
      state.binanceTimeConnect = payload
    })
    .addCase(updateHideOtherPairs, (state, { payload }) => {
      state.hideOtherPairs = payload
    })
    .addCase(updateResolution, (state, { payload }) => {
      state.resolution = payload
    })
    .addCase(updateChartType, (state, { payload }) => {
      state.chartType = payload
    })
    .addCase(getOrderbookAction.fulfilled, (state, { payload }) => {
      if (payload === undefined) {
        return
      }
      state.orderbook = payload
    })
    .addCase(updateOrderbookPrecisionAction, (state, { payload }) => {
      state.precision = payload
    })
    .addCase(updateShowListType, (state, { payload }) => {
      state.showListType = payload
    })
    .addCase(updateOrderbook, (state, { payload }) => {
      state.orderbook = payload
    })
    .addCase(clearOrderbookAction, (state) => {
      state.orderbook = {} as OrderbookData
    })
    .addCase(incrementalOrderbookAction, (state, { payload }) => {
      const { symbol, sequenceId, lastSequenceId, data } = payload
      if (symbol !== state.orderbook.symbol) return

      const { symbolId, price, buyOrders, sellOrders, direction } = data
      const { buyOrders: localBuyOrders, sellOrders: localSellOrders } =
        state.orderbook

      state.orderbook = {
        symbolId,
        symbol,
        price,
        sellOrders: insertAndMaintainOrder(
          localSellOrders,
          formatOrderbookMessage(sellOrders, true),
        ),
        buyOrders: insertAndMaintainOrder(
          localBuyOrders,
          formatOrderbookMessage(buyOrders, true),
          'desc',
        ),
        sequenceId,
        lastSequenceId,
        direction,
      }
    })
    .addCase(unshiftTickAction, (state, { payload }) => {
      const { symbolName, list } = payload

      const finalList = [...list, ...(state.tickList[symbolName] ?? [])]
      state.tickList[symbolName] = finalList.slice(0, 100)
    })
    .addCase(getRecentHistoryAction.fulfilled, (state, { payload }) => {
      const { symbolName, list } = payload ?? {}
      if (symbolName) {
        state.tickList[symbolName] = list!.slice(0, 100)
      }
    })
    .addCase(updateFavoriteResloution, (state, { payload }) => {
      if (payload.type === 'del' && state.favoriteResolution.length !== 0) {
        state.favoriteResolution = state.favoriteResolution.filter(
          (item) => item !== payload.value,
        )
      }
      if (payload.type === 'add' && state.favoriteResolution.length !== 3) {
        state.favoriteResolution = [...state.favoriteResolution, payload.value]
      }
    })
})
